import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { getApiErrorPayload } from "@/lib/api";
import { AssistantMessage, AssistantSuggestion, assistantAPI } from "../lib/assistantApi";

/** Turns replayed to the backend on each request; older turns are dropped to bound prompt size. */
const MAX_HISTORY_TURNS = 12;
const STORAGE_KEY = "assistant.conversation.v1";

interface PersistedConversation {
	/** Guards against showing one admin's conversation to the next one signed in on this tab. */
	userId: number;
	isOpen: boolean;
	messages: AssistantMessage[];
	draft: string;
}

interface AssistantContextValue {
	isAvailable: boolean;
	isOpen: boolean;
	open: () => void;
	close: () => void;
	toggle: () => void;
	messages: AssistantMessage[];
	draft: string;
	setDraft: (value: string) => void;
	sendMessage: (text: string) => void;
	isSending: boolean;
	/** Translation key of the last failure, or null. */
	errorKey: string | null;
	dismissError: () => void;
	clearConversation: () => void;
	suggestions: AssistantSuggestion[];
	isLoadingSuggestions: boolean;
}

const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

export const useAssistant = (): AssistantContextValue => {
	const context = useContext(AssistantContext);
	if (context === undefined) {
		throw new Error("useAssistant must be used within an AssistantProvider");
	}
	return context;
};

function readPersisted(userId: number | undefined): PersistedConversation | null {
	if (userId == null) return null;
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as PersistedConversation;
		return parsed.userId === userId ? parsed : null;
	} catch {
		// Corrupt or unavailable storage just means "start a fresh conversation".
		return null;
	}
}

/**
 * Holds the Smart Assistant conversation for the whole admin session.
 *
 * It is mounted once by `DashboardLayout`, above the routed `Outlet`, so navigating between admin
 * pages (including via assistant links) never remounts it. `sessionStorage` covers full page
 * reloads, keeping history, open state and the pending draft.
 */
export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { user, isAuthenticated } = useAuth();
	const { language } = useLanguage();

	const [isOpen, setIsOpen] = useState(false);
	const [messages, setMessages] = useState<AssistantMessage[]>([]);
	const [draft, setDraft] = useState("");
	const [errorKey, setErrorKey] = useState<string | null>(null);
	const [isRestored, setIsRestored] = useState(false);

	const { data: status } = useQuery({
		queryKey: ["assistant", "status"],
		queryFn: assistantAPI.getStatus,
		// The assistant API is restricted to the admin role (AssistantController); other roles get 403.
		enabled: isAuthenticated && user?.role?.roleKey === "admin",
		staleTime: 5 * 60 * 1000,
		retry: false,
	});
	const isAvailable = status?.available === true;

	const { data: suggestionsData, isLoading: isLoadingSuggestions } = useQuery({
		queryKey: ["assistant", "suggestions", language],
		queryFn: () => assistantAPI.getSuggestions(language),
		// Suggestions describe the store's current state, so they are only fetched when actually shown.
		enabled: isAvailable && isOpen && messages.length === 0,
		staleTime: 60 * 1000,
		retry: false,
	});

	// Restore once the signed-in user is known, so a conversation is never shown to another account.
	useEffect(() => {
		if (isRestored || !user?.id) return;
		const persisted = readPersisted(user.id);
		if (persisted) {
			setIsOpen(persisted.isOpen);
			setMessages(persisted.messages);
			setDraft(persisted.draft);
		}
		setIsRestored(true);
	}, [isRestored, user?.id]);

	useEffect(() => {
		if (!isRestored || !user?.id) return;
		try {
			const payload: PersistedConversation = { userId: user.id, isOpen, messages, draft };
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
		} catch {
			// Storage full or blocked: the in-memory conversation still works for this session.
		}
	}, [isRestored, user?.id, isOpen, messages, draft]);

	const chatMutation = useMutation({
		mutationFn: (history: AssistantMessage[]) =>
			assistantAPI.chat({ messages: history.slice(-MAX_HISTORY_TURNS), language }),
		onSuccess: response => {
			setMessages(previous => [...previous, { role: "assistant", content: response.reply }]);
		},
		onError: error => {
			setErrorKey(getApiErrorPayload(error).key);
		},
	});

	const sendMessage = useCallback(
		(text: string) => {
			const content = text.trim();
			if (!content || chatMutation.isPending) return;

			// The request is built outside the state updater: under StrictMode an updater may run
			// twice, which would send the turn twice.
			const next: AssistantMessage[] = [...messages, { role: "user", content }];
			setErrorKey(null);
			setDraft("");
			setMessages(next);
			chatMutation.mutate(next);
		},
		[chatMutation, messages],
	);

	const clearConversation = useCallback(() => {
		setMessages([]);
		setDraft("");
		setErrorKey(null);
	}, []);

	const value = useMemo<AssistantContextValue>(
		() => ({
			isAvailable,
			isOpen,
			open: () => setIsOpen(true),
			close: () => setIsOpen(false),
			toggle: () => setIsOpen(previous => !previous),
			messages,
			draft,
			setDraft,
			sendMessage,
			isSending: chatMutation.isPending,
			errorKey,
			dismissError: () => setErrorKey(null),
			clearConversation,
			suggestions: suggestionsData?.suggestions ?? [],
			isLoadingSuggestions,
		}),
		[
			isAvailable,
			isOpen,
			messages,
			draft,
			sendMessage,
			chatMutation.isPending,
			errorKey,
			clearConversation,
			suggestionsData,
			isLoadingSuggestions,
		],
	);

	return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};
